import React, { useState, useEffect } from 'react';
import { Save, Loader2, Type, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { getStorefrontSettings, updateStorefrontSettings } from '../firebase/storefront';
import { useToast } from './Toast';
import { TableSkeleton } from './LoadingSkeleton';
import { HelpTooltip, FeatureGuide, InfoBanner } from './HelpTooltip';

export function AdminLandingPageTab({ showToast }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    hero: false,
    about: false,
    programme: false,
    freebie: false
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await getStorefrontSettings();
      if (data) {
        setSettings(data);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error loading settings:', error);
      }
      showToast('Failed to load landing page settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateStorefrontSettings(settings);
      if (result.success) {
        showToast('Landing page content saved successfully', 'success');
      } else {
        showToast('Error saving: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error saving: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const isCurrentlyOpen = prev[section];
      // If opening a section, close all others (auto-accordion)
      if (!isCurrentlyOpen) {
        return {
          hero: false,
          about: false,
          programme: false,
          freebie: false,
          [section]: true
        };
      }
      // If closing, just close this one
      return { ...prev, [section]: false };
    });
  };

  if (loading) {
    return <TableSkeleton rows={5} />;
  }

  if (!settings) {
    return (
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
        <p className="text-gray-500">Failed to load settings</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Landing Page Content</h2>
          <HelpTooltip
            title="Landing Page Content"
            content="Edit the content that appears on your main landing page (homepage). This is separate from your storefront page. Changes here affect the marketing homepage with Hero, About, Programme, and Freebie sections."
          />
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] shadow-md hover:shadow-lg"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          <span>{saving ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      <FeatureGuide
        title="Landing Page Content Management"
        description="Manage all the text and content on your main landing page (homepage). This is your marketing site with Hero, About, Programme, and Freebie sections. This is separate from your Storefront page (/storefront) which is for product showcase."
        steps={[
          "Edit Hero section: badge, tagline, description",
          "Edit About section: title, quote, content, credentials, philosophy",
          "Edit Programme section: title, description, features, pricing",
          "Edit Freebie section: title, description, disclaimer",
          "Click 'Save Changes' to update the landing page",
          "Changes appear on your homepage immediately after saving"
        ]}
        tips={[
          "Landing page is your main marketing site (homepage)",
          "Storefront page (/storefront) is separate - for product showcase",
          "Keep content concise and compelling",
          "Test changes on the actual landing page after saving",
          "Hero section is the first thing visitors see"
        ]}
      />

      <InfoBanner type="info" title="Landing Page vs Storefront">
        <p className="text-sm mb-2">
          <strong>Landing Page</strong> (homepage): Your main marketing site with Hero, About, Programme, Freebie, and Booking sections. This is what visitors see at your main URL.
        </p>
        <p className="text-sm">
          <strong>Storefront</strong> (/storefront): A separate link-in-bio style page for showcasing and selling products. Managed in the Storefront tab.
        </p>
      </InfoBanner>

      <div className="space-y-3 sm:space-y-4">
        {/* Hero Section */}
        <div className={`bg-white rounded-xl shadow-md border-2 overflow-hidden transition-all duration-300 ${expandedSections.hero ? 'border-[#c5a059] shadow-lg' : 'border-gray-200 hover:border-gray-300'
          }`}>
          <button
            onClick={() => toggleSection('hero')}
            className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors min-h-[64px] sm:min-h-[72px]"
            aria-expanded={expandedSections.hero}
            aria-controls="hero-section-content"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`p-2 rounded-lg transition-colors flex-shrink-0 ${expandedSections.hero ? 'bg-blue-100' : 'bg-blue-50'
                }`}>
                <Type className={`transition-colors ${expandedSections.hero ? 'text-blue-700' : 'text-blue-600'}`} size={20} />
              </div>
              <div className="text-left flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 text-base sm:text-lg">Hero Section</h3>
                <p className="text-xs sm:text-sm text-gray-500 truncate sm:whitespace-normal">Main headline and tagline at the top of your landing page</p>
              </div>
            </div>
            <div className="flex-shrink-0 ml-3">
              {expandedSections.hero ? (
                <ChevronUp size={20} className="text-[#c5a059] transition-transform" />
              ) : (
                <ChevronDown size={20} className="text-gray-400 transition-transform" />
              )}
            </div>
          </button>

          <div
            id="hero-section-content"
            className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.hero ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            {expandedSections.hero && (
              <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-4 border-t border-gray-200 pt-4 sm:pt-6 animate-fadeIn">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Badge Text</label>
                  <input
                    type="text"
                    value={settings.heroBadge || ''}
                    onChange={(e) => handleChange('heroBadge', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                    placeholder="Cultural Rewiring & Self-Love"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Tagline (Part 1)</label>
                    <input
                      type="text"
                      value={settings.heroTagline || ''}
                      onChange={(e) => handleChange('heroTagline', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                      placeholder="Return to your"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Tagline (Highlighted Part)</label>
                    <input
                      type="text"
                      value={settings.heroTaglineHighlight || ''}
                      onChange={(e) => handleChange('heroTaglineHighlight', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                      placeholder="truest self."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                  <textarea
                    rows={3}
                    value={settings.heroDescription || ''}
                    onChange={(e) => handleChange('heroDescription', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] resize-none"
                    placeholder="Helping women heal, set boundaries..."
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* About Section */}
        <div className={`bg-white rounded-xl shadow-md border-2 overflow-hidden transition-all duration-300 ${expandedSections.about ? 'border-[#c5a059] shadow-lg' : 'border-gray-200 hover:border-gray-300'
          }`}>
          <button
            onClick={() => toggleSection('about')}
            className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors min-h-[64px] sm:min-h-[72px]"
            aria-expanded={expandedSections.about}
            aria-controls="about-section-content"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`p-2 rounded-lg transition-colors flex-shrink-0 ${expandedSections.about ? 'bg-green-100' : 'bg-green-50'
                }`}>
                <FileText className={`transition-colors ${expandedSections.about ? 'text-green-700' : 'text-green-600'}`} size={20} />
              </div>
              <div className="text-left flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 text-base sm:text-lg">About Section</h3>
                <p className="text-xs sm:text-sm text-gray-500 truncate sm:whitespace-normal">Who you are, your background, and philosophy</p>
              </div>
            </div>
            <div className="flex-shrink-0 ml-3">
              {expandedSections.about ? (
                <ChevronUp size={20} className="text-[#c5a059] transition-transform" />
              ) : (
                <ChevronDown size={20} className="text-gray-400 transition-transform" />
              )}
            </div>
          </button>

          <div
            id="about-section-content"
            className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.about ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            {expandedSections.about && (
              <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-4 border-t border-gray-200 pt-4 sm:pt-6 animate-fadeIn">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Section Title</label>
                  <input
                    type="text"
                    value={settings.aboutTitle || ''}
                    onChange={(e) => handleChange('aboutTitle', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                    placeholder="Who I Am"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Quote</label>
                  <textarea
                    rows={2}
                    value={settings.aboutQuote || ''}
                    onChange={(e) => handleChange('aboutQuote', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] resize-none"
                    placeholder="Fabiha is a self-love..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Content Paragraph 1</label>
                  <textarea
                    rows={2}
                    value={settings.aboutContent1 || ''}
                    onChange={(e) => handleChange('aboutContent1', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Content Paragraph 2</label>
                  <textarea
                    rows={2}
                    value={settings.aboutContent2 || ''}
                    onChange={(e) => handleChange('aboutContent2', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Credentials (one per line)</label>
                  <textarea
                    rows={5}
                    value={Array.isArray(settings.aboutCredentials) ? settings.aboutCredentials.join('\n') : ''}
                    onChange={(e) => handleChange('aboutCredentials', e.target.value.split('\n').filter(l => l.trim()))}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] resize-none"
                    placeholder="Trained in meditation facilitation&#10;LOA & mindset experience"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter one credential per line</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Philosophy Title</label>
                  <input
                    type="text"
                    value={settings.aboutPhilosophyTitle || ''}
                    onChange={(e) => handleChange('aboutPhilosophyTitle', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                    placeholder="My Philosophy"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Philosophy Text</label>
                  <textarea
                    rows={2}
                    value={settings.aboutPhilosophy || ''}
                    onChange={(e) => handleChange('aboutPhilosophy', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Philosophy Author</label>
                  <input
                    type="text"
                    value={settings.aboutPhilosophyAuthor || ''}
                    onChange={(e) => handleChange('aboutPhilosophyAuthor', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                    placeholder="— Fabiha"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Programme Section */}
        <div className={`bg-white rounded-xl shadow-md border-2 overflow-hidden transition-all duration-300 ${expandedSections.programme ? 'border-[#c5a059] shadow-lg' : 'border-gray-200 hover:border-gray-300'
          }`}>
          <button
            onClick={() => toggleSection('programme')}
            className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors min-h-[64px] sm:min-h-[72px]"
            aria-expanded={expandedSections.programme}
            aria-controls="programme-section-content"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`p-2 rounded-lg transition-colors flex-shrink-0 ${expandedSections.programme ? 'bg-purple-100' : 'bg-purple-50'
                }`}>
                <FileText className={`transition-colors ${expandedSections.programme ? 'text-purple-700' : 'text-purple-600'}`} size={20} />
              </div>
              <div className="text-left flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 text-base sm:text-lg">Programme Section</h3>
                <p className="text-xs sm:text-sm text-gray-500 truncate sm:whitespace-normal">Coaching programme details, features, and pricing</p>
              </div>
            </div>
            <div className="flex-shrink-0 ml-3">
              {expandedSections.programme ? (
                <ChevronUp size={20} className="text-[#c5a059] transition-transform" />
              ) : (
                <ChevronDown size={20} className="text-gray-400 transition-transform" />
              )}
            </div>
          </button>

          <div
            id="programme-section-content"
            className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.programme ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            {expandedSections.programme && (
              <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-4 border-t border-gray-200 pt-4 sm:pt-6 animate-fadeIn">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
                  <input
                    type="text"
                    value={settings.programmeTitle || ''}
                    onChange={(e) => handleChange('programmeTitle', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                    placeholder="12-Week 1:1 Coaching Programme"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                  <textarea
                    rows={2}
                    value={settings.programmeDescription || ''}
                    onChange={(e) => handleChange('programmeDescription', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Features (one per line)</label>
                  <textarea
                    rows={8}
                    value={Array.isArray(settings.programmeFeatures) ? settings.programmeFeatures.join('\n') : ''}
                    onChange={(e) => handleChange('programmeFeatures', e.target.value.split('\n').filter(l => l.trim()))}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] resize-none"
                    placeholder="Weekly 1:1 Zoom calls&#10;Tailored worksheets"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter one feature per line</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Price (£)</label>
                    <input
                      type="number"
                      value={settings.programmePrice || ''}
                      onChange={(e) => handleChange('programmePrice', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                      placeholder="555"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Original Price (£)</label>
                    <input
                      type="number"
                      value={settings.programmeOriginalPrice || ''}
                      onChange={(e) => handleChange('programmeOriginalPrice', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                      placeholder="1111"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Badge Text</label>
                    <input
                      type="text"
                      value={settings.programmeBadge || ''}
                      onChange={(e) => handleChange('programmeBadge', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                      placeholder="Intro Price"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Price Note</label>
                  <input
                    type="text"
                    value={settings.programmePriceNote || ''}
                    onChange={(e) => handleChange('programmePriceNote', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                    placeholder="Full price from early 2026"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment Note</label>
                  <input
                    type="text"
                    value={settings.programmePaymentNote || ''}
                    onChange={(e) => handleChange('programmePaymentNote', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                    placeholder="Payment plans available upon request."
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Freebie Section */}
        <div className={`bg-white rounded-xl shadow-md border-2 overflow-hidden transition-all duration-300 ${expandedSections.freebie ? 'border-[#c5a059] shadow-lg' : 'border-gray-200 hover:border-gray-300'
          }`}>
          <button
            onClick={() => toggleSection('freebie')}
            className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors min-h-[64px] sm:min-h-[72px]"
            aria-expanded={expandedSections.freebie}
            aria-controls="freebie-section-content"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`p-2 rounded-lg transition-colors flex-shrink-0 ${expandedSections.freebie ? 'bg-pink-100' : 'bg-pink-50'
                }`}>
                <FileText className={`transition-colors ${expandedSections.freebie ? 'text-pink-700' : 'text-pink-600'}`} size={20} />
              </div>
              <div className="text-left flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 text-base sm:text-lg">Freebie Section</h3>
                <p className="text-xs sm:text-sm text-gray-500 truncate sm:whitespace-normal">Free meditation signup form content</p>
              </div>
            </div>
            <div className="flex-shrink-0 ml-3">
              {expandedSections.freebie ? (
                <ChevronUp size={20} className="text-[#c5a059] transition-transform" />
              ) : (
                <ChevronDown size={20} className="text-gray-400 transition-transform" />
              )}
            </div>
          </button>

          <div
            id="freebie-section-content"
            className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.freebie ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            {expandedSections.freebie && (
              <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-4 border-t border-gray-200 pt-4 sm:pt-6 animate-fadeIn">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
                    <input
                      type="text"
                      value={settings.freebieTitle || ''}
                      onChange={(e) => handleChange('freebieTitle', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                      placeholder="Free Guided Meditation:"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Title (Highlighted)</label>
                    <input
                      type="text"
                      value={settings.freebieTitleHighlight || ''}
                      onChange={(e) => handleChange('freebieTitleHighlight', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                      placeholder='"Return to Self"'
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                  <textarea
                    rows={2}
                    value={settings.freebieDescription || ''}
                    onChange={(e) => handleChange('freebieDescription', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Button Text</label>
                  <input
                    type="text"
                    value={settings.freebieButtonText || ''}
                    onChange={(e) => handleChange('freebieButtonText', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                    placeholder="Send Me The Meditation"
                  />
                  <p className="text-xs text-gray-500 mt-1">The text shown on the signup button</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Disclaimer</label>
                  <textarea
                    rows={2}
                    value={settings.freebieDisclaimer || ''}
                    onChange={(e) => handleChange('freebieDisclaimer', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] resize-none"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



